/**
 * Multi-page text PDF builder (no external deps).
 * Image embedding is used by medplum-ui form exports; server copy omits images.
 * Keep text layout in sync with medplum-server/src/lib/signed-note-pdf.ts
 */

export type PdfLineStyle = 'heading' | 'body' | 'meta';

export interface PdfEmbeddedImage {
  width: number;
  height: number;
  /** Raw JPEG bytes for /DCTDecode embedding (preferred). */
  jpeg?: Uint8Array;
  /** Raw RGB bytes for /ASCIIHexDecode embedding (legacy). */
  rgb?: Uint8Array;
  /** When set, layout only — PNG is embedded post-build via pdf-lib. */
  overlayPngBytes?: Uint8Array;
  /** Rendered height in PDF points (default 60). */
  displayHeight?: number;
}

export interface SignatureImageOverlay {
  pageNumber: number;
  pngBytes: Uint8Array;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PdfSignatureBlock {
  label: string;
  image?: PdfEmbeddedImage;
  signedAtLabel?: string;
}

export interface PdfLine {
  text: string;
  style?: PdfLineStyle;
  /** Extra indent in points from the left margin. */
  indent?: number;
  image?: PdfEmbeddedImage;
  signatureBlock?: PdfSignatureBlock;
  /** Lines sharing a group id paginate together on the same page. */
  group?: string;
}

const PAGE_WIDTH = 612;
const PAGE_HEIGHT = 792;
const MARGIN_LEFT = 72;
const MARGIN_TOP = 72;
const MARGIN_BOTTOM = 54;
const FOOTER_HEIGHT = 18;
const TITLE_BLOCK_HEIGHT = 30;
const BODY_CHARS_PER_LINE = 78;
const INDENT_CHARS = 12;
const DEFAULT_IMAGE_HEIGHT = 60;
const IMAGE_GAP = 6;
const SIGNATURE_BOX_MIN_HEIGHT = 120;
const SIGNATURE_BOX_PADDING = 12;
const SIGNATURE_IMAGE_HEIGHT = 60;

function escapePdfText(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)');
}

function styleFont(style: PdfLineStyle): { name: 'F1' | 'F2'; size: number; leading: number } {
  switch (style) {
    case 'heading':
      return { name: 'F2', size: 12, leading: 18 };
    case 'meta':
      return { name: 'F1', size: 10, leading: 14 };
    default:
      return { name: 'F1', size: 11, leading: 15 };
  }
}

function wrapLines(text: string, maxChars: number): string[] {
  const lines: string[] = [];
  for (const raw of text.split(/\r?\n/)) {
    const trimmed = raw.trimEnd();
    if (trimmed.length <= maxChars) {
      lines.push(trimmed);
      continue;
    }
    let rest = trimmed;
    while (rest.length > maxChars) {
      let breakAt = rest.lastIndexOf(' ', maxChars);
      if (breakAt < 20) breakAt = maxChars;
      lines.push(rest.slice(0, breakAt).trimEnd());
      rest = rest.slice(breakAt).trimStart();
    }
    if (rest) lines.push(rest);
  }
  return lines;
}

function charsPerLineForIndent(indent = 0): number {
  const usableWidth = PAGE_WIDTH - MARGIN_LEFT - 72 - indent;
  return Math.max(24, Math.floor(usableWidth / 5.6));
}

interface PlacedLine extends PdfLine {
  y: number;
  imageDisplayWidth?: number;
  imageDisplayHeight?: number;
  signatureBoxHeight?: number;
}

interface PageLayout {
  title: boolean;
  lines: PlacedLine[];
  pageNumber: number;
}

function imageDisplaySize(line: PdfLine): { width: number; height: number } {
  const img = line.image!;
  const displayH = img.displayHeight ?? DEFAULT_IMAGE_HEIGHT;
  const aspect = img.width / img.height;
  const maxW = PAGE_WIDTH - MARGIN_LEFT - 72 - (line.indent ?? 0);
  const displayW = Math.min(maxW, displayH * aspect);
  return { width: displayW, height: displayH };
}

function signatureBlockHeight(block: PdfSignatureBlock): number {
  let height = SIGNATURE_BOX_PADDING * 2 + styleFont('meta').leading;
  if (block.image) {
    height += 8 + (block.image.displayHeight ?? SIGNATURE_IMAGE_HEIGHT);
  }
  if (block.signedAtLabel) {
    height += 6 + styleFont('meta').leading;
  }
  return Math.max(SIGNATURE_BOX_MIN_HEIGHT, height);
}

function expandPdfLines(lines: PdfLine[]): PdfLine[] {
  const expanded: PdfLine[] = [];
  for (const line of lines) {
    if (line.signatureBlock || line.image) {
      expanded.push(line);
      continue;
    }
    if (!line.text.trim()) {
      expanded.push({ text: '', style: line.style ?? 'body', indent: line.indent });
      continue;
    }
    const maxChars = charsPerLineForIndent(line.indent);
    for (const wrapped of wrapLines(line.text, maxChars)) {
      expanded.push({ text: wrapped, style: line.style, indent: line.indent });
    }
  }
  return expanded;
}

function lineHeight(line: PdfLine): number {
  if (line.signatureBlock) return signatureBlockHeight(line.signatureBlock) + IMAGE_GAP;
  if (line.image) return imageDisplaySize(line).height + IMAGE_GAP;
  return styleFont(line.style ?? 'body').leading;
}

function splitIntoGroups(lines: PdfLine[]): PdfLine[][] {
  const groups: PdfLine[][] = [];
  let current: PdfLine[] = [];
  let currentGroupId: string | undefined;

  const flush = (): void => {
    if (current.length > 0) {
      groups.push(current);
      current = [];
      currentGroupId = undefined;
    }
  };

  for (const line of lines) {
    const groupId = line.group;
    if (!groupId) {
      flush();
      groups.push([line]);
      continue;
    }
    if (currentGroupId && groupId !== currentGroupId) {
      flush();
    }
    currentGroupId = groupId;
    current.push(line);
  }
  flush();
  return groups;
}

function measureGroupHeight(lines: PdfLine[]): number {
  return expandPdfLines(lines).reduce((sum, line) => sum + lineHeight(line), 0);
}

function placeGroupLines(lines: PdfLine[], startY: number): { placed: PlacedLine[]; endY: number } {
  const placed: PlacedLine[] = [];
  let y = startY;

  for (const line of expandPdfLines(lines)) {
    if (line.signatureBlock) {
      const boxH = signatureBlockHeight(line.signatureBlock);
      const yBottom = y - boxH;
      placed.push({ ...line, y: yBottom, signatureBoxHeight: boxH });
      y = yBottom - IMAGE_GAP;
      continue;
    }

    if (line.image) {
      const { width: displayW, height: displayH } = imageDisplaySize(line);
      const yBottom = y - displayH;
      placed.push({
        ...line,
        y: yBottom,
        imageDisplayWidth: displayW,
        imageDisplayHeight: displayH,
      });
      y = yBottom - IMAGE_GAP;
      continue;
    }

    placed.push({ ...line, y });
    y -= styleFont(line.style ?? 'body').leading;
  }

  return { placed, endY: y };
}

function paginateLines(title: string, lines: PdfLine[]): PageLayout[] {
  const groups = splitIntoGroups(lines);
  const pages: PageLayout[] = [];
  let currentLines: PlacedLine[] = [];
  let pageIndex = 0;
  let y = PAGE_HEIGHT - MARGIN_TOP;

  const startPage = (): void => {
    if (pageIndex === 0 && title.trim()) {
      y -= TITLE_BLOCK_HEIGHT;
    }
  };

  const pushPage = (): void => {
    pages.push({
      title: pageIndex === 0 && !!title.trim(),
      lines: currentLines,
      pageNumber: pages.length + 1,
    });
    pageIndex += 1;
    currentLines = [];
    y = PAGE_HEIGHT - MARGIN_TOP;
  };

  startPage();

  for (const group of groups) {
    const minY = MARGIN_BOTTOM + FOOTER_HEIGHT;
    const groupHeight = measureGroupHeight(group);
    if (y - groupHeight < minY) {
      pushPage();
    }
    const { placed, endY } = placeGroupLines(group, y);
    currentLines.push(...placed);
    y = endY;
  }

  if (currentLines.length === 0 && pages.length === 0) {
    pushPage();
  } else if (currentLines.length > 0) {
    pushPage();
  }

  return pages;
}

function rgbToAsciiHexStream(rgb: Uint8Array): string {
  let hex = '';
  for (let i = 0; i < rgb.length; i++) {
    hex += rgb[i]!.toString(16).padStart(2, '0');
  }
  return `${hex}>\n`;
}

function uint8ToBinaryString(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
  return binary;
}

function encodeImageObject(imageId: number, img: PdfEmbeddedImage): string {
  if (img.rgb?.length) {
    const hexStream = rgbToAsciiHexStream(img.rgb);
    const hexLen = hexStream.length;
    return `${imageId} 0 obj<< /Type /XObject /Subtype /Image /Width ${img.width} /Height ${img.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /ASCIIHexDecode /Length ${hexLen} >>stream\n${hexStream}endstream\nendobj`;
  }

  if (img.jpeg?.length) {
    const binary = uint8ToBinaryString(img.jpeg);
    return `${imageId} 0 obj<< /Type /XObject /Subtype /Image /Width ${img.width} /Height ${img.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${img.jpeg.length} >>stream\n${binary}endstream\nendobj`;
  }

  return `${imageId} 0 obj<< /Type /XObject /Subtype /Image /Width 1 /Height 1 /ColorSpace /DeviceRGB /BitsPerComponent 8 /Length 3 >>stream\n\xFF\xFF\xFF\nendstream\nendobj`;
}

function buildPageStream(
  page: PageLayout & { totalPages: number },
  title: string,
  imageNameFor: (image: PdfEmbeddedImage) => string,
  overlays: SignatureImageOverlay[],
): { stream: string; imageNames: string[] } {
  const parts: string[] = [];
  const imageNames: string[] = [];

  const trackImage = (image: PdfEmbeddedImage): string => {
    const name = imageNameFor(image);
    if (!imageNames.includes(name)) imageNames.push(name);
    return name;
  };

  let textOpen = false;

  if (page.title) {
    parts.push('BT');
    parts.push('/F2 16 Tf');
    parts.push(`1 0 0 1 ${MARGIN_LEFT} ${PAGE_HEIGHT - MARGIN_TOP} Tm`);
    parts.push(`(${escapePdfText(title)}) Tj`);
    textOpen = true;
  }

  for (const line of page.lines) {
    if (line.signatureBlock) {
      if (textOpen) {
        parts.push('ET');
        textOpen = false;
      }
      const block = line.signatureBlock;
      const boxW = PAGE_WIDTH - MARGIN_LEFT - 72;
      const boxH = line.signatureBoxHeight ?? signatureBlockHeight(block);
      const boxX = MARGIN_LEFT;
      const boxBottom = line.y;

      parts.push('q');
      parts.push('0.92 0.95 1 rg');
      parts.push(`${boxX} ${boxBottom} ${boxW} ${boxH} re`);
      parts.push('f');
      parts.push('Q');

      let cursorY = boxBottom + boxH - SIGNATURE_BOX_PADDING;
      parts.push('BT');
      parts.push('/F1 10 Tf');
      parts.push('0.45 0.45 0.45 rg');
      parts.push(`1 0 0 1 ${boxX + SIGNATURE_BOX_PADDING} ${cursorY - 10} Tm`);
      parts.push(`(${escapePdfText(block.label)}) Tj`);
      parts.push('0 0 0 rg');
      parts.push('ET');
      cursorY -= 18;

      if (block.image) {
        const displayH = block.image.displayHeight ?? SIGNATURE_IMAGE_HEIGHT;
        const aspect = block.image.width / block.image.height;
        const displayW = Math.min(boxW - SIGNATURE_BOX_PADDING * 2, displayH * aspect);
        const imgBottom = cursorY - displayH;
        const imgX = boxX + SIGNATURE_BOX_PADDING;

        if (block.image.overlayPngBytes) {
          overlays.push({
            pageNumber: page.pageNumber,
            pngBytes: block.image.overlayPngBytes,
            x: imgX,
            y: imgBottom,
            width: displayW,
            height: displayH,
          });
        } else {
          const name = trackImage(block.image);
          parts.push('q');
          parts.push(`${displayW} 0 0 ${displayH} ${imgX} ${imgBottom} cm`);
          parts.push(`/${name} Do`);
          parts.push('Q');
        }
        cursorY = imgBottom - 6;
      }

      if (block.signedAtLabel) {
        parts.push('BT');
        parts.push('/F1 10 Tf');
        parts.push('0.45 0.45 0.45 rg');
        parts.push(`1 0 0 1 ${boxX + SIGNATURE_BOX_PADDING} ${cursorY - 10} Tm`);
        parts.push(`(${escapePdfText(block.signedAtLabel)}) Tj`);
        parts.push('0 0 0 rg');
        parts.push('ET');
      }
      continue;
    }

    if (line.image) {
      if (textOpen) {
        parts.push('ET');
        textOpen = false;
      }
      const name = trackImage(line.image);
      const x = MARGIN_LEFT + (line.indent ?? 0);
      const w = line.imageDisplayWidth ?? DEFAULT_IMAGE_HEIGHT * 2;
      const h = line.imageDisplayHeight ?? DEFAULT_IMAGE_HEIGHT;
      if (line.group) {
        const pad = 8;
        parts.push('q');
        parts.push('0.92 0.95 1 rg');
        parts.push(`${x - pad} ${line.y - pad} ${w + pad * 2} ${h + pad * 2} re`);
        parts.push('f');
        parts.push('Q');
      }
      parts.push('q');
      parts.push(`${w} 0 0 ${h} ${x} ${line.y} cm`);
      parts.push(`/${name} Do`);
      parts.push('Q');
      continue;
    }

    if (!textOpen) {
      parts.push('BT');
      textOpen = true;
    }
    const style = line.style ?? 'body';
    const { name, size } = styleFont(style);
    const x = MARGIN_LEFT + (line.indent ?? 0);
    parts.push(`/${name} ${size} Tf`);
    parts.push(`1 0 0 1 ${x} ${line.y} Tm`);
    parts.push(line.text.length ? `(${escapePdfText(line.text)}) Tj` : '() Tj');
  }

  if (textOpen) {
    parts.push('ET');
    textOpen = false;
  }

  const footer = `Page ${page.pageNumber} of ${page.totalPages}`;
  parts.push('BT');
  parts.push('/F1 9 Tf');
  parts.push(`1 0 0 1 ${PAGE_WIDTH / 2 - 24} ${MARGIN_BOTTOM / 2} Tm`);
  parts.push(`(${escapePdfText(footer)}) Tj`);
  parts.push('ET');

  return { stream: parts.join('\n'), imageNames };
}

function encodePdf(
  pageResults: Array<{ stream: string; imageNames: string[] }>,
  images: PdfEmbeddedImage[],
): Uint8Array {
  const objects: string[] = [];
  objects.push('');

  const catalogId = 1;
  const pagesId = 2;
  const firstPageId = 3;
  const pageCount = pageResults.length;
  const fontRegularId = firstPageId + pageCount * 2;
  const fontBoldId = fontRegularId + 1;
  const firstImageId = fontBoldId + 1;

  const pageIds: number[] = [];
  const contentIds: number[] = [];
  for (let i = 0; i < pageCount; i++) {
    pageIds.push(firstPageId + i * 2);
    contentIds.push(firstPageId + i * 2 + 1);
  }

  const imageIds = images.map((_, index) => firstImageId + index);
  const imageNameToId = new Map<string, number>();
  images.forEach((_, index) => {
    imageNameToId.set(`Im${index + 1}`, imageIds[index]!);
  });

  objects[catalogId] = `${catalogId} 0 obj<< /Type /Catalog /Pages ${pagesId} 0 R >>endobj`;
  objects[pagesId] =
    `${pagesId} 0 obj<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(' ')}] /Count ${pageCount} >>endobj`;

  for (let i = 0; i < pageCount; i++) {
    const pageId = pageIds[i]!;
    const contentId = contentIds[i]!;
    const { stream, imageNames } = pageResults[i]!;
    const streamLen = new TextEncoder().encode(stream).length;
    const xObjectSection =
      imageNames.length > 0
        ? ` /XObject<< ${imageNames.map((name) => `${name} ${imageNameToId.get(name)} 0 R`).join(' ')} >>`
        : '';
    objects[pageId] =
      `${pageId} 0 obj<< /Type /Page /Parent ${pagesId} 0 R /MediaBox [0 0 ${PAGE_WIDTH} ${PAGE_HEIGHT}] /Contents ${contentId} 0 R /Resources<< /Font<< /F1 ${fontRegularId} 0 R /F2 ${fontBoldId} 0 R >>${xObjectSection} >> >>endobj`;
    objects[contentId] = `${contentId} 0 obj<< /Length ${streamLen} >>stream\n${stream}\nendstream\nendobj`;
  }

  objects[fontRegularId] =
    `${fontRegularId} 0 obj<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>endobj`;
  objects[fontBoldId] =
    `${fontBoldId} 0 obj<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>endobj`;

  for (let i = 0; i < images.length; i++) {
    objects[imageIds[i]!] = encodeImageObject(imageIds[i]!, images[i]!);
  }

  let pdf = '%PDF-1.4\n';
  const offsets: number[] = [0];
  for (let i = 1; i < objects.length; i++) {
    offsets.push(pdf.length);
    pdf += `${objects[i]}\n`;
  }

  const xrefStart = pdf.length;
  pdf += `xref\n0 ${objects.length}\n`;
  pdf += '0000000000 65535 f \n';
  for (let i = 1; i < offsets.length; i++) {
    pdf += `${String(offsets[i]).padStart(10, '0')} 00000 n \n`;
  }
  pdf += `trailer<< /Size ${objects.length} /Root ${catalogId} 0 R >>\n`;
  pdf += `startxref\n${xrefStart}\n%%EOF`;
  return stringToLatin1Bytes(pdf);
}

function stringToLatin1Bytes(text: string): Uint8Array {
  const bytes = new Uint8Array(text.length);
  for (let i = 0; i < text.length; i++) bytes[i] = text.charCodeAt(i) & 0xff;
  return bytes;
}

/** Build a paginated PDF from structured lines. */
export function buildTextPdf(title: string, lines: PdfLine[]): Uint8Array {
  return buildTextPdfWithOverlays(title, lines).bytes;
}

/** Build PDF bytes plus signature PNG overlays for pdf-lib post-processing. */
export function buildTextPdfWithOverlays(
  title: string,
  lines: PdfLine[],
): { bytes: Uint8Array; overlays: SignatureImageOverlay[] } {
  const pages = paginateLines(title, lines);
  const totalPages = pages.length;
  const overlays: SignatureImageOverlay[] = [];

  const images: PdfEmbeddedImage[] = [];
  const imageKeyToName = new Map<PdfEmbeddedImage, string>();
  for (const page of pages) {
    for (const line of page.lines) {
      const image = line.image ?? line.signatureBlock?.image;
      if (image && !image.overlayPngBytes && !imageKeyToName.has(image)) {
        images.push(image);
        imageKeyToName.set(image, `Im${images.length}`);
      }
    }
  }

  const pageResults = pages.map((page) =>
    buildPageStream(
      { ...page, totalPages },
      title,
      (image) => imageKeyToName.get(image) ?? 'Im1',
      overlays,
    ),
  );
  return { bytes: encodePdf(pageResults, images), overlays };
}

function bodyToPdfLines(body: string): PdfLine[] {
  return body.split('\n').map((raw) => {
    const text = raw.trimEnd();
    if (!text) return { text: '', style: 'body' as const };

    const leadingSpaces = raw.length - raw.trimStart().length;
    const indent = Math.floor(leadingSpaces / 2) * INDENT_CHARS;
    const trimmed = text.trimStart();

    if (trimmed.endsWith(':') && trimmed.length < 64 && indent === 0) {
      return { text: trimmed, style: 'heading' as const };
    }
    if (trimmed.startsWith('Signed by:') || trimmed.startsWith('Completed:')) {
      return { text: trimmed, style: 'meta' as const };
    }
    return { text: trimmed, style: 'body' as const, indent: indent || undefined };
  });
}

/** Build a paginated PDF from a plain-text body (backward compatible). */
export function buildMinimalTextPdf(title: string, body: string): Uint8Array {
  return buildTextPdf(title, bodyToPdfLines(body));
}

export { wrapLines, BODY_CHARS_PER_LINE, INDENT_CHARS };
